import type { useGT } from 'gt-react';
import { formatCurrency } from '@erp/shared-utils';
import type {
  IncrementCheck,
  MoqCheck,
  OrderReadiness,
} from '@erp/shared-utils';

type Translate = ReturnType<typeof useGT>;

/** Short state word beside the stack icon (detail bar). */
export function readinessStateLabel(gt: Translate, r: OrderReadiness): string {
  if (r.state === 'ready') {
    return gt('Ready', {
      context: 'buying order readiness: can be placed as is',
    });
  }
  if (r.state === 'not_ready') {
    return gt('Not ready', {
      context: 'buying order readiness: nothing committed to release yet',
    });
  }
  if (!r.mov.met && r.mov.source === 'vendor') {
    return gt('Below vendor MOV', {
      context:
        'buying order readiness warning: under vendor minimum order value',
    });
  }
  if (!r.mov.met) {
    return gt('Below PO min', {
      context:
        'buying order readiness warning: orderable, but under the default PO minimum',
    });
  }
  if (!r.moq.met) {
    return gt('Below vendor MOQ', {
      context:
        'buying order readiness warning: at least one line under vendor MOQ',
    });
  }
  if (!r.increment.met) {
    return gt('Off pack multiple', {
      context:
        'buying order readiness warning: lines not on vendor pack multiple',
    });
  }
  return gt('Freight not prepaid', {
    context:
      'buying order readiness warning: orderable, but under the prepaid-freight threshold',
  });
}

/** One line for the hover title: the state and its main reason. */
export function readinessHoverSummary(
  gt: Translate,
  r: OrderReadiness,
): string {
  if (r.empty) {
    return gt('Not ready: no lines in this order yet', {
      context: 'buying order readiness hover, no committed or in-cart lines',
    });
  }
  if (!r.mov.met && r.mov.source === 'vendor') {
    return gt('Warning: {gap} under the {amount} minimum order value', {
      context: 'buying order readiness hover, under vendor MOV',
      gap: formatCurrency(r.mov.gap),
      amount: formatCurrency(r.mov.amount),
    });
  }
  if (!r.mov.met) {
    return gt('Warning: {gap} under the {amount} default PO minimum', {
      context: 'buying order readiness hover, under the default PO minimum',
      gap: formatCurrency(r.mov.gap),
      amount: formatCurrency(r.mov.amount),
    });
  }
  if (!r.moq.met) {
    return moqHoverWarning(gt, r.moq.belowCount);
  }
  if (!r.increment.met) {
    return incrementHoverWarning(gt, r.increment.offCount);
  }
  if (!r.freight.met) {
    return gt('Warning: {gap} short of {amount} prepaid freight', {
      context: 'buying order readiness hover, under prepaid freight',
      gap: formatCurrency(r.freight.gap),
      amount: formatCurrency(r.freight.amount),
    });
  }
  if (r.withinBuffer) return withinBufferHover(gt, r);
  return gt('Ready to order', {
    context: 'buying order readiness hover, all checks met',
  });
}

/** Ready, but met within the buffer: the PO minimum first. */
function withinBufferHover(gt: Translate, r: OrderReadiness): string {
  const pct = String(r.bufferPct);
  if (r.mov.inBuffer) {
    return gt(
      'Ready: {cushion} over the {amount} minimum, within the {pct}% buffer',
      {
        context:
          'buying order readiness hover: can be placed, but over the PO minimum by less than the org buffer, so a cancelled line could drop it under',
        cushion: formatCurrency(r.mov.cushion),
        amount: formatCurrency(r.mov.amount),
        pct,
      },
    );
  }
  return gt(
    'Ready: {cushion} over the {amount} prepaid freight, within the {pct}% buffer',
    {
      context:
        'buying order readiness hover: can be placed, but over the prepaid-freight threshold by less than the org buffer',
      cushion: formatCurrency(r.freight.cushion),
      amount: formatCurrency(r.freight.amount),
      pct,
    },
  );
}

function moqHoverWarning(gt: Translate, belowCount: number): string {
  return belowCount === 1
    ? gt('Warning: 1 line under the vendor min order qty', {
        context: 'buying order readiness hover, one line below vendor MOQ',
      })
    : gt('Warning: {count} lines under the vendor min order qty', {
        context: 'buying order readiness hover, lines below vendor MOQ',
        count: String(belowCount),
      });
}

function incrementHoverWarning(gt: Translate, offCount: number): string {
  return offCount === 1
    ? gt('Warning: 1 line off the vendor pack multiple', {
        context: 'buying order readiness hover, one line off vendor increment',
      })
    : gt('Warning: {count} lines off the vendor pack multiple', {
        context: 'buying order readiness hover, lines off vendor increment',
        count: String(offCount),
      });
}

/** Popover row value for the vendor MOQ check. */
export function moqCheckRowValue(gt: Translate, moq: MoqCheck): string {
  if (moq.met) {
    return moq.lineCount === 1
      ? gt('1 line', {
          context: 'readiness popover: the one line meets the vendor MOQ',
        })
      : gt('all {count} lines', {
          context: 'readiness popover: every line meets the vendor MOQ',
          count: String(moq.lineCount),
        });
  }
  return moq.belowCount === 1
    ? gt('1 line under', {
        context: 'readiness popover: one line below the vendor MOQ',
      })
    : gt('{count} lines under', {
        context: 'readiness popover: lines below the vendor MOQ',
        count: String(moq.belowCount),
      });
}

/** Popover row value for the pack-multiple check. */
export function incrementCheckRowValue(
  gt: Translate,
  increment: IncrementCheck,
): string {
  if (increment.met) {
    return gt('on multiple', {
      context: 'readiness popover: quantities on pack multiple',
    });
  }
  return increment.offCount === 1
    ? gt('1 line off', {
        context: 'readiness popover: one line off vendor pack multiple',
      })
    : gt('{count} lines off', {
        context: 'readiness popover: lines off vendor pack multiple',
        count: String(increment.offCount),
      });
}
